import apiClient from "./apiClient";
import {
  DEFAULT_EXPIRY_THRESHOLD_MONTHS,
  notifyPharmacyExpiryThresholdChanged,
  parseExpiryThresholdMonths,
  readExpiryThresholdMonthsFromStorage,
  writeExpiryThresholdMonthsToStorage,
} from "../utils/medicineExpiryThreshold";

const SETTINGS_BASE = "/pharmacy-settings";

/**
 * Load near-expiry threshold (months). Uses API when available, else localStorage.
 */
export const getPharmacyExpiryThresholdMonths = async (organizationId) => {
  try {
    const response = await apiClient.get(`${SETTINGS_BASE}/expiry-threshold`);
    const months = parseExpiryThresholdMonths(
      response.data?.expiryThresholdMonths ??
        response.data?.nearExpiryThresholdMonths ??
        response.data?.months,
      null
    );
    if (months != null) {
      writeExpiryThresholdMonthsToStorage(organizationId, months);
      return months;
    }
  } catch (error) {
    if (error.response?.status && error.response.status !== 404) {
      console.warn("[pharmacySettings] API load failed, using local storage:", error.message);
    }
  }

  const stored = readExpiryThresholdMonthsFromStorage(organizationId);
  return stored ?? DEFAULT_EXPIRY_THRESHOLD_MONTHS;
};

export const savePharmacyExpiryThresholdMonths = async (organizationId, months) => {
  const safeMonths = writeExpiryThresholdMonthsToStorage(organizationId, months);

  try {
    await apiClient.put(`${SETTINGS_BASE}/expiry-threshold`, {
      expiryThresholdMonths: safeMonths,
    });
  } catch (error) {
    if (error.response?.status && error.response.status !== 404) {
      console.warn("[pharmacySettings] API save failed, stored locally only:", error.message);
    }
  }

  notifyPharmacyExpiryThresholdChanged(safeMonths);
  return safeMonths;
};

/**
 * Notify backend to flag low-stock / reorder when issuing near-expiry batch.
 * Fails silently if endpoint is not implemented yet.
 */
export const triggerNearExpiryLowStockAlert = async ({
  medicineId,
  medicineName,
  batchNumber,
  expiryDate,
  quantity,
  source = "ISSUE",
}) => {
  if (!medicineId || !expiryDate) return null;

  const payload = {
    medicineId,
    medicineName: medicineName || null,
    batchNumber: batchNumber || null,
    expiryDate,
    quantity: quantity ?? null,
    source,
    reason: "NEAR_EXPIRY_ISSUE",
  };

  try {
    const response = await apiClient.post(
      `${SETTINGS_BASE}/near-expiry-low-stock`,
      payload
    );
    return response.data;
  } catch (error) {
    if (error.response?.status !== 404) {
      console.warn("[pharmacySettings] Near-expiry low-stock alert skipped:", error.message);
    }
    return null;
  }
};
